import React, { useEffect, useState } from 'react'
import { apiGet } from '../../../generic/Api_Functions'
import { SmartSelect } from '../../../generic/smartSelect'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { Button, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material'
import { SmartDialog } from '../../../generic/utilities/SmartDialog'
import SelectPreset from './SelectPreset'
import ReplyAllIcon from '@mui/icons-material/ReplyAll'
import AlertUI from '../../../generic/AlertUI'
import { useSmartInterval } from '../../../generic/hooks/useSmartInterval'

/**
 * @typedef {import('../../../generic/utilities/objectStructures/home/home').Device} Device Device object
 * @typedef {import('../../../generic/utilities/objectStructures/home/home').Resource} Resource Resource object
 * @typedef {import('../../../generic/utilities/objectStructures/sendprogram/sendProgram').SendProgram} SendProgram SendProgram object
 * @typedef {import('../../../generic/utilities/objectStructures/sendprogram/sendProgram').SendProgramAction} SendProgramAction SendProgramAction object
 */

/**
 * @param {Object} props Props object
 * @param {Device} props.device Device object
 * @param {Resource} props.resource Resource object
 * @param {Resource} props.fixture Fixture object
 * @param {SendProgram} props.data SendProgram object
 * @param {React.Dispatch<SendProgramAction>} props.dispatchdata Dispatch function
 * @param {Object} props.alert Alert object
 * @returns
 */
const SendProgram = ({ device, resource, fixture, data, dispatchdata, alert }) => {
  /** @type {[string[], React.Dispatch<SetStateAction<string[]>>]} */
  const [programList, setProgramList] = useState([])
  const [isPartPresetEmpty, setPartPresetEmpty] = useState(false)
  const [openSelectPresetDialog, setOpenSelectPresetDialog] = useState(false)
  const [currentSelectedPreset, setCurrentSelectedPreset] = useState({})
  const [AlertElement, showAlert] = AlertUI()
  const [partPresets, setPartPresets] = useState([])
  const { t_ } = useSmartTranslation()
  const [partJobLinkerProgramSelector, setPartJobLinkerProgramSelector] = useState(false)

  const getIntervalTableData = async () => {
    return await apiGet('fixturePartSendPreset/' + resource.id + '/' + fixture.id)
  }
  const updateSmartInterval = intervalData => {
    let isPartPresetEmpty = true
    if (intervalData.length > 0 && intervalData[0].source === 'PART') {
      isPartPresetEmpty = false
    }
    setPartPresetEmpty(isPartPresetEmpty)
    setPartPresets(intervalData)
  }
  const startTableInterval = useSmartInterval(getIntervalTableData, 1000, updateSmartInterval)

  useEffect(() => {
    const getProgramList = async () => {
      let programList
      if (!partJobLinkerProgramSelector) {
        programList = await apiGet('programs/device/' + device.id)
      } else {
        programList = await apiGet('programs/device/' + device.id + '/part/' + resource.id)
      }
      setProgramList(programList)
    }
    getProgramList()
  }, [device.id, resource.id, partJobLinkerProgramSelector])

  useEffect(() => {
    if (alert) {
      showAlert(alert)
    }
  }, [alert, showAlert])

  const getPreset = () => {
    if (data.preset) {
      return 'X: ' + data.preset.x + '; Y: ' + data.preset.y + '; Z: ' + data.preset.z + '; A: ' + data.preset.a + '; B: ' + data.preset.b + '; C: ' + data.preset.c
    }
  }

  const getSelectPresetTitle = () => {
    if (isPartPresetEmpty) return t_('Part Reference') + ': ' + resource.reference.code + ' - ' + t_('Fixture') + ': ' + fixture.reference.name
    return t_('Part') + ': ' + resource.code + ' - ' + t_('Fixture') + ': ' + fixture.reference.name
  }
  return (
    <>
      {AlertElement}
      <SmartDialog
        setOpen={openSelectPresetDialog}
        title={{
          icon: <ReplyAllIcon color='primary' />,
          render: <Typography color='primary' variant='h5'>{t_('Select Preset')}</Typography>
        }}
        renderComponent={
          <>
            <Typography color='primary' variant='h6'>{getSelectPresetTitle()}</Typography>
            <SelectPreset
              presets={partPresets}
              onRowClick={(row) => {
                setCurrentSelectedPreset({ x: row.x, y: row.y, z: row.z, a: row.a, b: row.b, c: row.c })
              }}
            />
          </>
        }
        acceptCallback={() => {
          if (currentSelectedPreset === null || Object.keys(currentSelectedPreset).length === 0) {
            return
          }
          dispatchdata({ type: 'SET_PRESET', currentPreset: currentSelectedPreset })
          setOpenSelectPresetDialog(false)
        }}
        cancelCallback={() => setOpenSelectPresetDialog(false)}
      />
      <Typography variant='h6' color='primary'>
        {t_('Device') + ': ' + device.name + ' - ' + t_('Resource') + ': ' + resource.code + ' - ' + t_('Fixture') + ': ' + fixture.code}
      </Typography>
      <div style={{ marginTop: '1rem', gap: '1rem', alignItems: 'center', display: 'grid', gridTemplateColumns: 'max-content 1fr max-content' }}>
        <Checkbox
          checked={data.sendPreset}
          onChange={(event, newValue) => {
            startTableInterval(newValue)
            dispatchdata({ type: 'SET_SEND_PRESET', send: newValue })
            if (!newValue) {
              dispatchdata({ type: 'SET_PRESET', currentPreset: null })
            }
          }}
        />
        <TextField
          disabled
          sx={{ flexGrow: 1 }}
          InputLabelProps={{ shrink: true }} value={getPreset()} label={t_('Selected preset')}
        />
        <Button
          onClick={() => setOpenSelectPresetDialog(true)}
          disabled={resource.type.name !== 'Part' || !data.sendPreset}
          variant='outlined'
        >{t_('Select Preset')}
        </Button>
        <Checkbox
          checked={data.sendProgram}
          onChange={(event, newValue) => {
            dispatchdata({ type: 'SET_SEND_PROGRAM', send: newValue })
            if (!newValue) {
              dispatchdata({ type: 'SET_PROGRAM_LIST', currentProgramList: [] })
            }
          }}
        />
        <SmartSelect
          disabled={!data.sendProgram}
          width='30rem'
          label={t_('Send Program List')}
          multiple
          value={data.programList}
          onChange={(value) => {
            dispatchdata({ type: 'SET_PROGRAM_LIST', currentProgramList: value })
          }}
          selectableOptions={programList}
        />
        <FormControlLabel
          marginLeft='3px'
          control={
            <Checkbox
              checked={partJobLinkerProgramSelector}
              onChange={(event, newValue) => {
                setPartJobLinkerProgramSelector(!partJobLinkerProgramSelector)
              }}
              inputProps={{ 'aria-label': 'controlled' }}
            />
      }
          label={(
            <Typography color='primary' fontSize='0.9rem'>{t_('PART PROGRAMS')}</Typography>)}
        />
      </div>
    </>
  )
}

export default SendProgram
